import {
  type CollisionDetection,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  useDndContext,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { IconChart1 } from 'central-icons/IconChart1';
import { IconImages1 } from 'central-icons/IconImages1';
import { IconLayoutColumn } from 'central-icons/IconLayoutColumn';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import { IconText1 } from 'central-icons/IconText1';
import classNames from 'classnames';
import {
  type CSSProperties,
  createContext,
  type FocusEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  blockLeaves,
  type ResolvedLayoutPage,
} from './convert-grid-report-to-flow';
import styles from './ReportFlow.module.css';
import {
  MAX_REPORT_FLOW_COLUMNS,
  type ReportFlowAction,
} from './report-flow-reducer';
import type { ReportFlowLeafBlock } from './report-flow-types';

const PAGE_DROP_PREFIX = 'pagedrop:';
const SIDE_DROP_PREFIX = 'side:';
const SIDE_SLOT_PREFIX = 'sideslot:';
const ORIGIN_DROP_PREFIX = 'origin:';
const GAP_DROP_PREFIX = 'gap:';
const LEAF_DRAG_PREFIX = 'leaf:';
const HOVER_REVEAL_DELAY_MS = 500;

export const EDITOR_AUTO_SCROLL = {
  threshold: { x: 0, y: 0.08 },
  acceleration: 6,
};

const isSideTargetId = (id: string) =>
  id.startsWith(SIDE_DROP_PREFIX) || id.startsWith(SIDE_SLOT_PREFIX);

export const dropCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return rectIntersection({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (container) => !isSideTargetId(String(container.id))
    ),
  });
};

/** Drag id for a single column inside a columns block. */
const leafDragId = (itemId: string) => `${LEAF_DRAG_PREFIX}${itemId}`;

const parseLeafDragId = (id: string): string | null =>
  id.startsWith(LEAF_DRAG_PREFIX) ? id.slice(LEAF_DRAG_PREFIX.length) : null;

const sideDropId = (side: 'left' | 'right', blockId: string) =>
  `${SIDE_DROP_PREFIX}${side}:${blockId}`;

const sideZoneIdOf = (id: string) =>
  id.startsWith(SIDE_SLOT_PREFIX) ? id.slice(SIDE_SLOT_PREFIX.length) : id;

const parseSideDropId = (
  id: string
): { side: 'left' | 'right'; blockId: string } | null => {
  const zoneId = sideZoneIdOf(id);
  if (!zoneId.startsWith(SIDE_DROP_PREFIX)) return null;
  const rest = zoneId.slice(SIDE_DROP_PREFIX.length);
  const separator = rest.indexOf(':');
  if (separator === -1) return null;
  const side = rest.slice(0, separator);
  if (side !== 'left' && side !== 'right') return null;
  return { side, blockId: rest.slice(separator + 1) };
};

const gapDropId = (pageId: string, index: number) =>
  `${GAP_DROP_PREFIX}${pageId}:${index}`;

const parseGapDropId = (
  id: string
): { pageId: string; index: number } | null => {
  if (!id.startsWith(GAP_DROP_PREFIX)) return null;
  const rest = id.slice(GAP_DROP_PREFIX.length);
  const separator = rest.lastIndexOf(':');
  if (separator === -1) return null;
  const index = Number(rest.slice(separator + 1));
  if (!Number.isInteger(index) || index < 0) return null;
  return { pageId: rest.slice(0, separator), index };
};

const blockEntries = (pages: ResolvedLayoutPage[]) =>
  pages.flatMap((page) =>
    page.blocks.map((block, index) => ({ pageId: page.id, block, index }))
  );

const findBlock = (pages: ResolvedLayoutPage[], blockId: string) =>
  blockEntries(pages).find(({ block }) => block.id === blockId);

const soleItemOf = (pages: ResolvedLayoutPage[], blockId: string) => {
  const block = findBlock(pages, blockId)?.block;
  return block && block.kind !== 'columns' ? block.item.id : undefined;
};

const draggedItemIdOf = (pages: ResolvedLayoutPage[], dragId: string) =>
  parseLeafDragId(dragId) ?? soleItemOf(pages, dragId);

const leavesOf = (pages: ResolvedLayoutPage[]) =>
  blockEntries(pages).flatMap(({ block }) => blockLeaves(block));

const edgeItemOf = (
  pages: ResolvedLayoutPage[],
  targetId: string,
  side: 'left' | 'right'
): string | undefined => {
  const leafItemId = parseLeafDragId(targetId);
  if (leafItemId !== null) {
    return leavesOf(pages).some((leaf) => leaf.item.id === leafItemId)
      ? leafItemId
      : undefined;
  }
  const block = findBlock(pages, targetId)?.block;
  if (!block) return undefined;
  const leaves = blockLeaves(block);
  return (side === 'left' ? leaves[0] : leaves[leaves.length - 1])?.item.id;
};

const rowLeavesOf = (pages: ResolvedLayoutPage[], itemId: string) =>
  blockEntries(pages)
    .map(({ block }) => blockLeaves(block))
    .find((leaves) => leaves.some((leaf) => leaf.item.id === itemId));

const keepsRowOrder = (
  pages: ResolvedLayoutPage[],
  itemId: string,
  besideItemId: string,
  side: 'left' | 'right'
) => {
  const leaves = rowLeavesOf(pages, besideItemId) ?? [];
  const from = leaves.findIndex((leaf) => leaf.item.id === itemId);
  if (from === -1) return false;
  const at =
    leaves.findIndex((leaf) => leaf.item.id === besideItemId) +
    (side === 'right' ? 1 : 0);
  return at === from || at === from + 1;
};

const dropTarget = (pages: ResolvedLayoutPage[], overId: string) => {
  const gap = parseGapDropId(overId);
  if (gap) return gap;
  if (!overId.startsWith(PAGE_DROP_PREFIX)) return null;
  const pageId = overId.slice(PAGE_DROP_PREFIX.length);
  const page = pages.find((candidate) => candidate.id === pageId);
  return page ? { pageId, index: page.blocks.length } : null;
};

export const isSingleItemDrag = (
  pages: ResolvedLayoutPage[],
  dragId: string
): boolean =>
  parseLeafDragId(dragId) !== null || soleItemOf(pages, dragId) !== undefined;

export const resolveDropAction = (
  pages: ResolvedLayoutPage[],
  activeId: string,
  overId: string
): ReportFlowAction | null => {
  if (activeId === overId) return null;
  // Single-item drags route through the item move so they can join other columns.
  const draggedItemId = draggedItemIdOf(pages, activeId);
  const sideDrop = parseSideDropId(overId);

  if (sideDrop) {
    if (!draggedItemId) return null;
    const besideItemId = edgeItemOf(pages, sideDrop.blockId, sideDrop.side);
    if (!besideItemId || besideItemId === draggedItemId) return null;
    if (keepsRowOrder(pages, draggedItemId, besideItemId, sideDrop.side)) {
      return null;
    }
    return {
      type: 'REPORT_FLOW_MOVE_ITEM',
      payload: { itemId: draggedItemId, besideItemId, side: sideDrop.side },
    };
  }

  const target = dropTarget(pages, overId);
  if (!target) return null;
  const origin = findBlock(pages, activeId);
  let toIndex = target.index;
  if (origin && origin.pageId === target.pageId) {
    if (origin.index === toIndex || origin.index + 1 === toIndex) return null;
    if (origin.index < toIndex) toIndex -= 1;
  }

  if (draggedItemId) {
    return {
      type: 'REPORT_FLOW_MOVE_ITEM',
      payload: { itemId: draggedItemId, toPageId: target.pageId, toIndex },
    };
  }
  if (!origin) return null;
  return {
    type: 'REPORT_FLOW_MOVE_BLOCK_TO',
    payload: {
      fromPageId: origin.pageId,
      fromIndex: origin.index,
      toPageId: target.pageId,
      toIndex,
    },
  };
};

export const acceptsSideDrop = (
  pages: ResolvedLayoutPage[],
  dragId: string,
  blockId: string
): boolean => {
  const block = findBlock(pages, blockId)?.block;
  if (!block) return false;
  const leaves = blockLeaves(block);
  const draggedItemId = draggedItemIdOf(pages, dragId);
  return (
    leaves.length < MAX_REPORT_FLOW_COLUMNS ||
    leaves.some((leaf) => leaf.item.id === draggedItemId)
  );
};

type SideDropSlot = {
  zoneId: string;
  index: number;
  widthCols: number;
  rowWidthCols: number[];
  tight: boolean;
};

const resolveSideDropSlot = (
  pages: ResolvedLayoutPage[],
  activeId: string,
  overId: string
): { blockId: string; slot: SideDropSlot } | null => {
  const action = parseSideDropId(overId)
    ? resolveDropAction(pages, activeId, overId)
    : null;
  if (action?.type !== 'REPORT_FLOW_MOVE_ITEM') return null;
  const { itemId, besideItemId, side } = action.payload;
  if (!besideItemId) return null;
  const row = blockEntries(pages).find(({ block }) =>
    blockLeaves(block).some((leaf) => leaf.item.id === besideItemId)
  );
  const dragged = leavesOf(pages).find((leaf) => leaf.item.id === itemId);
  if (!row || !dragged || !acceptsSideDrop(pages, activeId, row.block.id)) {
    return null;
  }
  const leaves = blockLeaves(row.block);
  const at = leaves.findIndex((leaf) => leaf.item.id === besideItemId);
  return {
    blockId: row.block.id,
    slot: {
      zoneId: sideZoneIdOf(overId),
      index: at + (side === 'right' ? 1 : 0),
      widthCols: dragged.item.position.width,
      rowWidthCols: leaves.map((leaf) => leaf.item.position.width),
      tight: [...leaves, dragged].every((leaf) => leaf.kind !== 'text'),
    },
  };
};

const useSideDropSlot = (
  pages: ResolvedLayoutPage[],
  blockId: string
): SideDropSlot | null => {
  const { active, over } = useDndContext();
  const activeId = active ? String(active.id) : null;
  const overId = over ? String(over.id) : null;
  return useMemo(() => {
    if (activeId === null || overId === null) return null;
    const target = resolveSideDropSlot(pages, activeId, overId);
    return target?.blockId === blockId ? target.slot : null;
  }, [pages, activeId, overId, blockId]);
};

const slotColumnsTemplate = ({
  index,
  widthCols,
  rowWidthCols,
}: SideDropSlot) =>
  [...rowWidthCols.slice(0, index), widthCols, ...rowWidthCols.slice(index)]
    .map((width) => `${width}fr`)
    .join(' ');

const slotRowStyle = (
  slot: SideDropSlot | null,
  isColumnsRow: boolean
): CSSProperties | undefined => {
  if (!slot) return undefined;
  if (isColumnsRow) {
    return {
      '--rf-columns-slot': slotColumnsTemplate(slot),
    } as CSSProperties;
  }
  return { '--rf-row-grow': slot.rowWidthCols[0] } as CSSProperties;
};

const useIsNoopDrop = (pages: ResolvedLayoutPage[]) => {
  const { active, over } = useDndContext();
  const activeId = active ? String(active.id) : null;
  const overId = over ? String(over.id) : null;
  return useMemo(
    () =>
      activeId !== null &&
      (overId === null || resolveDropAction(pages, activeId, overId) === null),
    [pages, activeId, overId]
  );
};

const useOriginDrop = (
  pages: ResolvedLayoutPage[],
  dragId: string,
  isDragging: boolean,
  setDragNodeRef: (node: HTMLElement | null) => void
) => {
  const { setNodeRef } = useDroppable({
    id: `${ORIGIN_DROP_PREFIX}${dragId}`,
    disabled: !isDragging,
  });
  const isNoop = useIsNoopDrop(pages);
  const ref = useCallback(
    (node: HTMLElement | null) => {
      setDragNodeRef(node);
      setNodeRef(node);
    },
    [setDragNodeRef, setNodeRef]
  );
  return {
    ref,
    draggingClassName: isNoop
      ? styles.dropPlaceholder
      : styles.editorBlockDragging,
  };
};

const SideDropSlotContext = createContext<SideDropSlot | null>(null);

const DropSlot = ({ slot }: { slot: SideDropSlot }) => {
  const { setNodeRef } = useDroppable({
    id: `${SIDE_SLOT_PREFIX}${slot.zoneId}`,
  });
  return (
    <div
      ref={setNodeRef}
      className={styles.sideDropSlot}
      style={{ '--rf-slot-grow': slot.widthCols } as CSSProperties}
      data-testid="report-flow-side-drop-slot"
    />
  );
};

const leafDragMeta = (
  leaf: ReportFlowLeafBlock
): { icon: ReactNode; label: string } => {
  if (leaf.kind === 'text') {
    return {
      icon: <IconText1 size={16} />,
      label: leaf.headings[0]?.text || 'Text',
    };
  }
  if (leaf.kind === 'image') {
    return { icon: <IconImages1 size={16} />, label: 'Image' };
  }
  return {
    icon: <IconChart1 size={16} />,
    label: leaf.item.title || 'Chart',
  };
};

export const ReportFlowDragGhost = ({
  pages,
  activeDragId,
}: {
  pages: ResolvedLayoutPage[];
  activeDragId: string | null;
}) => {
  const meta = useMemo(() => {
    if (!activeDragId) return null;
    const leafItemId = parseLeafDragId(activeDragId);
    if (leafItemId !== null) {
      const leaf = leavesOf(pages).find(
        (candidate) => candidate.item.id === leafItemId
      );
      return leaf ? leafDragMeta(leaf) : null;
    }
    const block = findBlock(pages, activeDragId)?.block;
    if (!block) return null;
    return block.kind === 'columns'
      ? {
          icon: <IconLayoutColumn size={16} />,
          label: `${block.columns.length} columns`,
        }
      : leafDragMeta(block);
  }, [activeDragId, pages]);

  return (
    <DragOverlay dropAnimation={null}>
      {meta && (
        <div className={styles.dragGhost}>
          {meta.icon}
          <span>{meta.label}</span>
        </div>
      )}
    </DragOverlay>
  );
};

/** Reveals after a long hover, or at once when keyboard focus enters the zone. */
export const useHoverReveal = (enabled: boolean) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const timerRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current === null) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const hide = useCallback(() => {
    cancel();
    setHovered(false);
    setFocused(false);
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  return {
    revealed: enabled && (hovered || focused),
    hide,
    hoverProps: {
      onMouseEnter: () => {
        if (!enabled) return;
        cancel();
        timerRef.current = window.setTimeout(
          () => setHovered(true),
          HOVER_REVEAL_DELAY_MS
        );
      },
      onMouseLeave: () => {
        cancel();
        setHovered(false);
      },
      onFocus: () => {
        if (enabled) setFocused(true);
      },
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
          return;
        }
        setFocused(false);
      },
    },
  };
};

const SideDropZone = ({
  side,
  blockId,
}: {
  side: 'left' | 'right';
  blockId: string;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: sideDropId(side, blockId),
  });
  return (
    <div
      ref={setNodeRef}
      className={classNames(
        styles.sideDrop,
        side === 'left' ? styles.sideDropLeft : styles.sideDropRight,
        isOver && styles.sideDropOver
      )}
    />
  );
};

const AddBlockButtons = ({
  onAddChart,
  onAddText,
}: {
  onAddChart: () => void;
  onAddText: () => void;
}) => (
  <>
    <button
      type="button"
      className={styles.skeletonButton}
      onClick={onAddChart}
      data-testid="report-flow-add-chart"
    >
      <IconChart1 size={14} />
      Add chart
    </button>
    <button
      type="button"
      className={styles.skeletonButton}
      onClick={onAddText}
      data-testid="report-flow-add-text"
    >
      <IconText1 size={14} />
      Add text
    </button>
  </>
);

export const GapDropZone = ({
  pages,
  pageId,
  index,
  isDragging,
  expandOnHover,
  onAddChart,
  onAddText,
}: {
  pages: ResolvedLayoutPage[];
  pageId: string;
  index: number;
  isDragging: boolean;
  expandOnHover: boolean;
  onAddChart: (index: number) => void;
  onAddText: (index: number) => void;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: gapDropId(pageId, index),
    disabled: !isDragging,
  });
  // Read at render time: the active rect is unmeasured when drag-start fires.
  const { active } = useDndContext();
  const dropSlotHeightPx = active?.rect.current.initial?.height ?? null;
  const { revealed, hide, hoverProps } = useHoverReveal(
    !isDragging && expandOnHover
  );
  const expanded = revealed && !isDragging;

  useEffect(() => {
    if (isDragging) hide();
  }, [isDragging, hide]);

  const isNoop = useIsNoopDrop(pages);
  const isLiveOver = isOver && !isNoop;
  const showDropSlot = isDragging && isLiveOver && dropSlotHeightPx !== null;

  return (
    <div
      ref={setNodeRef}
      className={classNames(
        styles.gapDrop,
        isDragging && styles.gapDropActive,
        isLiveOver && styles.gapDropOver,
        showDropSlot && styles.gapDropSlot,
        expanded && styles.gapDropExpanded
      )}
      style={
        showDropSlot
          ? ({ '--rf-gap-slot-h': `${dropSlotHeightPx}px` } as CSSProperties)
          : undefined
      }
      data-testid="report-flow-gap-drop"
      {...hoverProps}
    >
      {expandOnHover && !isDragging && (
        <div
          className={classNames(
            styles.gapSkeleton,
            !expanded && styles.addControlsConcealed
          )}
        >
          <div className={styles.skeletonBlock}>
            <AddBlockButtons
              onAddChart={() => {
                hide();
                onAddChart(index);
              }}
              onAddText={() => {
                hide();
                onAddText(index);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const DraggableBlock = ({
  pages,
  blockId,
  showSideDrops,
  toolbar,
  readOnly,
  anchorId,
  isSectionBreak,
  onActivate,
  children,
}: {
  pages: ResolvedLayoutPage[];
  blockId: string;
  showSideDrops: boolean;
  toolbar: ReactNode;
  readOnly: boolean;
  anchorId?: string;
  isSectionBreak: boolean;
  onActivate?: () => void;
  children: ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: blockId,
    disabled: readOnly,
  });
  const origin = useOriginDrop(pages, blockId, isDragging, setNodeRef);
  const slot = useSideDropSlot(pages, blockId);
  const isColumnsRow = findBlock(pages, blockId)?.block.kind === 'columns';
  const rowSlot = isColumnsRow ? null : slot;

  return (
    <div
      ref={origin.ref}
      id={anchorId}
      data-flow-block-id={blockId}
      className={classNames(
        readOnly
          ? styles.viewBlock
          : [styles.editorBlock, styles.editorBlockGrab],
        isDragging && origin.draggingClassName,
        slot && styles.editorBlockSlotOpen,
        readOnly && isSectionBreak && styles.viewSectionBreak
      )}
      data-testid={readOnly ? undefined : 'report-flow-editor-block'}
      {...(readOnly ? {} : { ...attributes, ...listeners })}
      onKeyDown={
        readOnly || !onActivate
          ? undefined
          : (event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key !== 'Enter' && event.key !== 'F2') return;
              event.preventDefault();
              onActivate();
            }
      }
    >
      {toolbar}
      {showSideDrops && !isDragging && (
        <>
          <SideDropZone side="left" blockId={blockId} />
          <SideDropZone side="right" blockId={blockId} />
        </>
      )}
      <SideDropSlotContext.Provider value={isColumnsRow ? slot : null}>
        <div
          className={classNames(
            styles.blockRow,
            rowSlot?.tight && styles.blockRowTight
          )}
          style={slotRowStyle(slot, isColumnsRow)}
        >
          {rowSlot?.index === 0 ? <DropSlot slot={rowSlot} /> : null}
          <div className={styles.blockRowContent}>{children}</div>
          {rowSlot && rowSlot.index > 0 ? <DropSlot slot={rowSlot} /> : null}
        </div>
      </SideDropSlotContext.Provider>
    </div>
  );
};

const ColumnAddZone = ({
  side,
  onAdd,
}: {
  side: 'left' | 'right';
  onAdd: () => void;
}) => {
  const { revealed, hide, hoverProps } = useHoverReveal(true);

  return (
    <div
      className={classNames(
        styles.columnAddZone,
        side === 'left' ? styles.columnAddZoneLeft : styles.columnAddZoneRight,
        revealed && styles.columnAddZoneRevealed
      )}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      {...hoverProps}
    >
      <button
        type="button"
        className={classNames(
          styles.columnAddButton,
          !revealed && styles.addControlsConcealed
        )}
        aria-label="Add chart here"
        data-testid="report-flow-add-column-chart"
        onClick={() => {
          hide();
          onAdd();
        }}
      >
        <IconPlusSmall size={16} />
      </button>
    </div>
  );
};

export const DraggableColumnLeaf = ({
  pages,
  itemId,
  columnIndex,
  isLastColumn,
  isSelected,
  onSelect,
  children,
  isEditorDragging,
  showSideDrops,
  onAddBefore,
  onAddAfter,
  readOnly,
}: {
  pages: ResolvedLayoutPage[];
  itemId: string;
  columnIndex: number;
  isLastColumn: boolean;
  readOnly: boolean;
  isSelected: boolean;
  onSelect: (itemId: string) => void;
  children: ReactNode;
  isEditorDragging: boolean;
  showSideDrops: boolean;
  onAddBefore?: () => void;
  onAddAfter?: () => void;
}) => {
  // useDraggable, not useSortable: columns are not part of the page's vertical sort list.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: leafDragId(itemId),
    disabled: readOnly,
  });
  const origin = useOriginDrop(
    pages,
    leafDragId(itemId),
    isDragging,
    setNodeRef
  );
  const slot = useContext(SideDropSlotContext);

  return (
    <>
      {slot?.index === columnIndex ? <DropSlot slot={slot} /> : null}
      <div
        ref={origin.ref}
        className={classNames(
          styles.columnCell,
          !readOnly && styles.columnCellDraggable,
          isSelected && styles.columnCellSelected,
          isDragging && origin.draggingClassName
        )}
        data-testid="report-flow-column-cell"
        {...(readOnly
          ? {}
          : {
              ...attributes,
              ...listeners,
              onPointerDown: (event: React.PointerEvent) => {
                // Keep the parent block's drag from starting too.
                event.stopPropagation();
                listeners?.onPointerDown?.(event);
              },
              onClick: () => onSelect(itemId),
            })}
      >
        {showSideDrops && !isDragging && (
          <>
            <SideDropZone side="left" blockId={leafDragId(itemId)} />
            <SideDropZone side="right" blockId={leafDragId(itemId)} />
          </>
        )}
        {!isEditorDragging && onAddBefore && (
          <ColumnAddZone side="left" onAdd={onAddBefore} />
        )}
        {!isEditorDragging && onAddAfter && (
          <ColumnAddZone side="right" onAdd={onAddAfter} />
        )}
        {children}
      </div>
      {isLastColumn && slot?.index === columnIndex + 1 ? (
        <DropSlot slot={slot} />
      ) : null}
    </>
  );
};

export const PageSkeletons = ({
  pages,
  pageId,
  isDragging,
  collapsible,
  onAddChart,
  onAddText,
}: {
  pages: ResolvedLayoutPage[];
  pageId: string;
  isDragging: boolean;
  /** A page with content keeps the tiles folded into a strip until a long hover. */
  collapsible: boolean;
  onAddChart: () => void;
  onAddText: () => void;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${PAGE_DROP_PREFIX}${pageId}`,
    disabled: !isDragging,
  });
  const isNoop = useIsNoopDrop(pages);
  const isLiveOver = isOver && !isNoop;
  const { revealed, hide, hoverProps } = useHoverReveal(
    collapsible && !isDragging
  );
  useEffect(() => {
    if (isDragging) hide();
  }, [isDragging, hide]);
  const expanded = !collapsible || (revealed && !isDragging);
  return (
    <div
      ref={setNodeRef}
      className={classNames(
        styles.pageSkeletons,
        !expanded && styles.pageSkeletonsCollapsed,
        isDragging && styles.pageSkeletonsDropActive,
        isLiveOver && styles.pageSkeletonsOver
      )}
      data-testid="report-flow-page-skeletons"
      data-expanded={expanded || undefined}
      {...hoverProps}
    >
      <div
        className={classNames(
          styles.skeletonBlock,
          !expanded && styles.addControlsConcealed
        )}
      >
        <AddBlockButtons
          onAddChart={() => {
            hide();
            onAddChart();
          }}
          onAddText={() => {
            hide();
            onAddText();
          }}
        />
      </div>
    </div>
  );
};
